import { useEffect, useState } from 'react';
import * as apiProducts from 'api/apiProducts';
import * as apiSets from 'api/apiProductSets';
import * as apiServices from 'api/apiServices';
import * as apiWarranties from 'api/apiWarranties';
import { useAuth } from 'context/AuthContext';
import { isManagerUser, isSellerUser } from 'utils/helpers/access/hasUserAccess';

const useCatalogSource = <T,>(load: () => Promise<T>, enabled: boolean, scope: string) => {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [revision, setRevision] = useState(0);
    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        setData(null);
        setLoading(true);
        void load().then((result) => {
            if (!cancelled) setData(result);
        }).catch(() => {
            if (!cancelled) setData(null);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [load, enabled, scope, revision]);
    return { data, loading, retry: () => setRevision((value) => value + 1) };
};

const useCatalogDashboard = () => {
    const { auth } = useAuth();
    const enabled = isManagerUser(auth?.user) || isSellerUser(auth?.user);
    const scope = `${auth?.user?.ID}:${auth?.user?.access?.role}:${auth?.user?.admin}:${auth?.user?.access?.salesAccess}`;
    const products = useCatalogSource(apiProducts.getStatistics, enabled, scope);
    const sets = useCatalogSource(apiSets.getStatistics, enabled, scope);
    const services = useCatalogSource(apiServices.getStatistics, enabled, scope);
    const warranties = useCatalogSource(apiWarranties.getStatistics, enabled, scope);
    return { enabled, products, sets, services, warranties };
};

export default useCatalogDashboard;
